import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import type { HubConnection, IRetryPolicy } from '@microsoft/signalr';
import type { PortalHubClientEvents, PortalHubServerMethods } from './contracts/portalHub';

export type HubEventMap = Record<string, (...args: never[]) => void>;
export type HubMethodMap = Record<string, (...args: never[]) => Promise<unknown>>;

export type NoServerMethods = Record<never, never>;

export type TypedHubConnection<TEvents extends HubEventMap, TMethods extends HubMethodMap = NoServerMethods> = {
  readonly connection: HubConnection;
  readonly hubName: string;
  start(): Promise<void>;
  stop(): Promise<void>;
  isDisconnected(): boolean;
  isConnecting(): boolean;
  on<TEventName extends keyof TEvents & string>(eventName: TEventName, handler: TEvents[TEventName]): () => void;
  invoke<TMethodName extends keyof TMethods & string>(
    methodName: TMethodName,
    ...args: Parameters<TMethods[TMethodName]>
  ): ReturnType<TMethods[TMethodName]>;
  onClose(callback: (error?: Error) => void): () => void;
  onReconnecting(callback: (error?: Error) => void): () => void;
  onReconnected(callback: () => void): () => void;
  dispose(): Promise<void>;
};

export type CreateTypedHubConnectionOptions = {
  url: string;
  hubName: string;
  accessTokenFactory?: () => Promise<string>;
};

const RECONNECT_DELAYS_MS = [0, 2_000, 10_000] as const;

const reconnectForever: IRetryPolicy = {
  nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
    RECONNECT_DELAYS_MS[previousRetryCount] ?? 10_000,
};

export const ACCESS_TOKEN_FACTORY_TIMEOUT_MS = 10_000;

function withLastKnownGoodTokenFallback(factory: () => Promise<string>): () => Promise<string> {
  let lastKnownGoodToken = '';

  return () =>
    new Promise<string>((resolve, reject) => {
      const fallbackTimer = setTimeout(() => resolve(lastKnownGoodToken), ACCESS_TOKEN_FACTORY_TIMEOUT_MS);
      factory().then(
        (token) => {
          clearTimeout(fallbackTimer);
          lastKnownGoodToken = token;
          resolve(token);
        },
        (error: unknown) => {
          clearTimeout(fallbackTimer);
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    });
}

export function createTypedHubConnection<TEvents extends HubEventMap, TMethods extends HubMethodMap = NoServerMethods>(
  options: CreateTypedHubConnectionOptions,
): TypedHubConnection<TEvents, TMethods> {
  const connection = new HubConnectionBuilder()
    .withUrl(options.url, {
      accessTokenFactory:
        options.accessTokenFactory === undefined
          ? undefined
          : withLastKnownGoodTokenFallback(options.accessTokenFactory),
    })
    .withAutomaticReconnect(reconnectForever)
    .withStatefulReconnect()
    .build();

  const closeCallbacks = new Set<(error?: Error) => void>();
  const reconnectingCallbacks = new Set<(error?: Error) => void>();
  const reconnectedCallbacks = new Set<() => void>();

  connection.onclose((error) => closeCallbacks.forEach((callback) => callback(error)));
  connection.onreconnecting((error) => reconnectingCallbacks.forEach((callback) => callback(error)));
  connection.onreconnected(() => reconnectedCallbacks.forEach((callback) => callback()));

  return {
    connection,
    hubName: options.hubName,
    start: () => connection.start(),
    stop: () => connection.stop(),
    isDisconnected: () => connection.state === HubConnectionState.Disconnected,
    isConnecting: () => connection.state === HubConnectionState.Connecting,
    on(eventName, handler) {
      const signalRHandler = handler as Parameters<HubConnection['on']>[1];
      connection.on(eventName, signalRHandler);
      return () => connection.off(eventName, signalRHandler);
    },
    invoke(methodName, ...args) {
      type Result = ReturnType<TMethods[typeof methodName]>;
      return connection.invoke(methodName, ...args) as Result;
    },
    onClose(callback) {
      closeCallbacks.add(callback);
      return () => closeCallbacks.delete(callback);
    },
    onReconnecting(callback) {
      reconnectingCallbacks.add(callback);
      return () => reconnectingCallbacks.delete(callback);
    },
    onReconnected(callback) {
      reconnectedCallbacks.add(callback);
      return () => reconnectedCallbacks.delete(callback);
    },
    dispose: () => connection.stop(),
  };
}

export type PortalHubConnection = TypedHubConnection<PortalHubClientEvents, PortalHubServerMethods>;

export type HubFactoryOptions = {
  emsServerUrl: string;
  accessTokenFactory: () => Promise<string>;
};

export type HubFactory = {
  createPortalHubConnection(): PortalHubConnection;
};

export function createHubFactory(options: HubFactoryOptions): HubFactory {
  const emsServerUrl = options.emsServerUrl.replace(/\/+$/, '');
  const { accessTokenFactory } = options;

  return {
    createPortalHubConnection() {
      return createTypedHubConnection({
        url: `${emsServerUrl}/hub/portal`,
        hubName: 'PortalHubConnection',
        accessTokenFactory,
      });
    },
  };
}
